import { memo, useCallback, useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { commonStyles } from '../../../../common/styles';
import AppColors from '../../../../common/colors';
import { MainStackProps } from '../../../../types/screens';
import Utils from '../../../../utils/utils';
import { OptionsType } from '../../../../common/screen_params';
import States from '../../../../common/states';
import { persistedStorage } from '../../../../common/storage';
import localization from '../../../../common/localization';
import { useToast } from 'react-native-toast-notifications';

type Props = MainStackProps<'OptionsDialogMobile'>;

const OptionsDialogMobile = memo<Props>(({ navigation, route: { params } }) => {
  const toast = useToast();

  const [title, items, index]: [string, (number | string)[], number] =
    useMemo(() => {
      const locale = localization.settings;
      switch (params.type) {
        case OptionsType.bufferLength:
          const bufferValue = States.mainState._settings?.http_caching.value;
          const bufferList = States.mainState._settings?.http_caching.list;
          if (bufferList === undefined || bufferValue === undefined) break;
          return [
            locale.streamBuffer,
            bufferList ?? [],
            bufferList?.indexOf(
              +(States.mainState._settings?.http_caching.value ?? 3000),
            ) ?? 0,
          ];
        case OptionsType.pipMode:
          return [
            locale.pipMode,
            [locale.pipModeAlways, locale.pipModeInApp, locale.pipModeNever],
            persistedStorage.getNumber('pipMode') ?? 0,
          ];
      }
      return ['', Utils.emptyArray, 0];
    }, [params.type]);

  const onPress = useMemo(() => {
    switch (params.type) {
      case OptionsType.bufferLength:
        return (index: number) => {
          const value = States.mainState._settings?.http_caching.list[index];
          if (value) {
            States.mainState
              .setBufferLength(value)
              .catch(() => toast.show(localization.error.settingNotSave));
          }
          navigation.pop();
        };
      case OptionsType.pipMode:
        return (index: number) => {
          persistedStorage.set('pipMode', index);
          navigation.pop();
        };
    }
  }, [params.type]);
  const close = useCallback(() => navigation.pop(), []);

  const visibleItems = useMemo(
    () =>
      items.map((e, i) => (
        <OptionItem
          key={i}
          text={e}
          index={i}
          checked={i === index}
          onPress={onPress}
        />
      )),
    [index],
  );

  return (
    <View style={styles.container}>
      <Pressable onPress={close} style={commonStyles.fill} />
      <View style={styles.dialogContainer}>
        <Text style={styles.title}>{title}</Text>
        {visibleItems}
      </View>
    </View>
  );
});
OptionsDialogMobile.displayName = 'OptionsDialogMobile';

type ItemProps = {
  text: string | number;
  checked?: boolean;
  index: number;
  onPress?: (index: number) => void;
};

const OptionItem = memo<ItemProps>(({ text, checked, index, onPress }) => {
  const press = useCallback(() => onPress?.(index), [index, onPress]);

  return (
    <Pressable onPress={press} style={styles.itemContainer}>
      <Text style={styles.itemText}>{text}</Text>
      <Radio checked={checked} />
    </Pressable>
  );
});

const Radio = memo<Pick<ItemProps, 'checked'>>(({ checked }) => {
  const style = useMemo(
    () =>
      StyleSheet.compose(styles.radioCenter, {
        display: checked ? 'flex' : 'none',
      }),
    [checked],
  );
  return (
    <View
      style={
        checked ? styles.radioContainerChecked : styles.radioContainerUnchecked
      }>
      <View style={style} />
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dialogContainer: {
    backgroundColor: AppColors.containerGray,
    marginHorizontal: 40,
    paddingTop: 24,
    paddingBottom: 12,
    paddingHorizontal: 24,
    borderRadius: 16,
  },
  title: {
    ...commonStyles.text,
    color: 'white',
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 6,
    textAlign: 'center',
  },
  itemContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  itemText: {
    ...commonStyles.text,
    flex: 1,
    fontSize: 20,
    fontWeight: '400',
    color: 'white',
  },
  radioContainerUnchecked: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 150,
    borderWidth: 1,
    borderColor: '#6C7072',
  },
  radioContainerChecked: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 150,
    backgroundColor: AppColors.mainBlue,
  },
  radioCenter: {
    width: 8,
    height: 8,
    borderRadius: 150,
    backgroundColor: 'white',
  },
});

export default OptionsDialogMobile;
